import { Button } from "@headlessui/react";
import { HomeIcon } from "@heroicons/react/20/solid";
import { PrinterIcon } from "@heroicons/react/24/outline";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { Link } from "react-router-dom";
import { forwardRef } from "react";

const breadcrumbsVariants = cva("breadcrumbs", {
  variants: {
    color: {
      default: "text-black",
      red: "text-lf-red-dark",
      blue: "text-lf-blue-dark",
      lightblue: "text-lf-blue-light",
    },
  },
  defaultVariants: {
    color: "default",
  },
});

interface BreadcrumbsProps extends VariantProps<typeof breadcrumbsVariants> {
  pages: {
    name: string;
    href: string;
    current: boolean;
    testId?: string;
  }[];
  showPrintButton?: boolean;
  /** A crumb (or the Home icon, as "Home" / "/") was clicked. */
  onNavigate?: (name: string, href: string) => void;
  /** The print button was clicked. */
  onPrint?: () => void;
}

const Breadcrumbs = forwardRef<HTMLDivElement, BreadcrumbsProps>(
  (props, ref) => {
    const { pages, showPrintButton = false, onNavigate, onPrint } = props;
    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className="flex justify-between border-b xl:border-r border-gray-200 bg-white"
      >
        <ol
          role="list"
          className="flex w-full mx-0 space-x-4 px-4 sm:px-6 lg:px-8"
        >
          <li className="flex">
            <div className="flex items-center">
              <Link
                to="/"
                onClick={() => onNavigate?.("Home", "/")}
                className="text-gray-400 hover:text-gray-500"
              >
                <HomeIcon
                  aria-hidden="true"
                  className="h-5 w-5 flex-shrink-0"
                />
                <span className="sr-only">Home</span>
              </Link>
            </div>
          </li>
          {pages.map((page, index) => (
            <li
              key={page.name}
              className={
                index === pages.length - 1
                  ? "flex-shrink min-w-32"
                  : "flex-1 min-w-fit max-w-fit"
              }
            >
              <div className="flex items-center">
                <svg
                  fill="currentColor"
                  viewBox="0 0 24 44"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                  className="h-full w-6 flex-shrink-0 text-gray-200"
                >
                  <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                </svg>
                <Link
                  to={page.href}
                  onClick={() => onNavigate?.(page.name, page.href)}
                  aria-current={page.current ? "page" : undefined}
                  className="ml-4 text-sm text-nowrap overflow-hidden text-ellipsis font-medium text-gray-500 hover:text-gray-700"
                  data-testid={page.testId}
                >
                  {page.name}
                </Link>
              </div>
            </li>
          ))}
        </ol>
        {/* This could be the start of a general barButton API -- for now, only printing is supported */}
        {showPrintButton && (
          <Button
            onClick={() => {
              onPrint?.();
              window?.print();
            }}
            className="hidden md:block bg-white cursor-pointer hover:bg-gray-100 active:bg-gray-300 rounded-md px-4 mx-2 my-1"
          >
            <PrinterIcon
              aria-hidden="true"
              className="h-5 w-5 flex-shrink-0 text-lf-blue-dark"
            />
          </Button>
        )}
      </nav>
    );
  },
);

Breadcrumbs.displayName = "Breadcrumbs";

export default Breadcrumbs;
export { breadcrumbsVariants };
export type { BreadcrumbsProps };
